import React from "react";

import type { ErrorMap } from "./ErrorHandling";

/**
 * Extracts the key path from a selector function (lens).
 * Example: p => p.range.from  →  ["range", "from"]
 */
function extractPath<TModel, TValue>(selector: (model: TModel) => TValue): (string | number)[] {
	const path: (string | number)[] = [];

	const proxy = new Proxy(
		{},
		{
			get(_, key) {
				path.push(key as string | number);
				return proxy;
			},
		},
	);

	selector(proxy as TModel);
	return path;
}

export type BoundedValue<TValue> = {
	value: TValue;
	onChangeValue: (newValue: TValue) => void;
	errorKey: string;
	errorMap?: ErrorMap;
};

export function useBoundDeep<TModel, TValue>(
	model: TModel,
	setModel: React.Dispatch<React.SetStateAction<TModel>>,
	selector: (m: TModel) => TValue,
	errors?: ErrorMap,
	setErrors?: React.Dispatch<React.SetStateAction<ErrorMap>>,
): BoundedValue<TValue> {
	// Precompute the path once
	const path = React.useMemo(() => extractPath(selector), [selector]);

	// Convert path into a string key
	const errorKey = path.join(".");

	const value = selector(model);

	const onChangeValue = React.useCallback(
		(newValue: TValue) => {
			setModel((prev) => {
				// Clone root shallowly
				const updated = { ...prev };
				let cursor = updated as Record<string | number, unknown>;

				// Clone deep on path
				for (let i = 0; i < path.length - 1; i++) {
					const key = path[i];
					if (key === undefined) continue;
					const original = cursor[key];
					cursor[key] = Array.isArray(original)
						? [...(original as unknown[])]
						: { ...(original as Record<string, unknown>) };
					cursor = cursor[key] as Record<string | number, unknown>;
				}

				const lastKey = path[path.length - 1];
				if (lastKey !== undefined) {
					cursor[lastKey] = newValue;
				}

				return updated;
			});

			// Automatically clear field error
			if (setErrors) {
				setErrors((prevErrors: ErrorMap) => {
					if (prevErrors != null && errorKey in prevErrors) {
						const next: ErrorMap = { ...prevErrors };
						next[errorKey] = "";
						return next;
					}
					return prevErrors;
				});
			}
		},
		[setModel, setErrors, errorKey, path],
	);

	return {
		value,
		onChangeValue,
		errorKey,
		errorMap: errors,
	};
}
